import { useState } from "react";
import type { CaseView, PackView } from "../api";
import { SectionLabel } from "./chrome";
import { LetCraigDoIt } from "./Brand";
import { Typed } from "./typography";

/* ═══════════════════════════════════════════════════════════════════════
   Your file.

   The reassurance mechanism of the whole product: the customer watches
   their answers land, and can correct anything crAIg got wrong by tapping
   it. Nothing they say is trapped in a transcript they can't see or fix.

   Deliberately NOT here: the outstanding-items list. It still runs every
   turn — it keeps crAIg thorough and gates the signature — but it is
   written for the reviewer, and a customer opening their file to "43
   questions still to put to them" reads it as a wall of homework. crAIg
   raises what's missing conversationally instead.

   Also not here: any knowledge of what industry this is. Every
   domain-specific sentence comes from pack.copy.
   ═══════════════════════════════════════════════════════════════════════ */

const money = (s: string | undefined): number => {
  if (!s) return 0;
  const m = s.match(/[\d,]+(\.\d+)?/);
  return m ? Number(m[0].replace(/,/g, "")) || 0 : 0;
};

export function FileDrawer({
  kase,
  pack,
  wide,
  open,
  onClose,
  onCorrect,
  onOpenDrop,
  children,
}: {
  kase: CaseView;
  pack: PackView;
  wide: boolean;
  open: boolean;
  onClose(): void;
  onCorrect(name: string, value: string): void;
  onOpenDrop(): void;
  /** The declaration panel — passed in so this file stays about the file. */
  children?: React.ReactNode;
}) {
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState("");

  /* Their own answers, in their own words. Never an internal value — the
     drawer's whole job is showing somebody what crAIg wrote down, and one
     of our ids is not something they said. */
  const filled = pack.fields
    .filter((f) => !f.internal)
    .filter((f) => (kase.fields[f.name] ?? "").trim());
  const flagFor = (name: string) => kase.flags.find((f) => f.ref === name)?.text;

  const buckets = Object.entries(kase.items).filter(([, items]) => items?.length);
  const evidence = pack.copy.evidence;

  const optionLabel = (id: string) => {
    for (const list of pack.checklists) {
      const opt = list.options.find((o) => o.id === id);
      if (opt) return opt.label;
    }
    return id;
  };

  return (
    <aside
      className={wide ? "sticky top-4 shrink-0" : "fixed inset-y-0 right-0 z-50 scroll-y"}
      style={
        wide
          ? { width: 340, display: open ? "block" : "none" }
          : {
              width: "min(92vw, 400px)",
              background: "var(--bg)",
              boxShadow: "var(--shadow-lg)",
              transform: open ? "translateX(0)" : "translateX(101%)",
              transition: "transform .28s cubic-bezier(.22,1,.36,1)",
              padding: "0.75rem",
            }
      }
    >
      <div className="card overflow-hidden">
        <div
          className="flex items-center gap-2 px-4 py-3"
          style={{ borderBottom: "1px solid var(--line)" }}
        >
          <span style={{ fontWeight: 650, fontSize: 14 }}>Your details</span>
          <span className="ml-auto" style={{ fontSize: 12.5, color: "var(--ink-3)" }}>
            {kase.progress}%
          </span>
          {!wide && (
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              style={{ background: "none", border: "none", cursor: "pointer", color: "var(--ink-3)" }}
            >
              ✕
            </button>
          )}
        </div>

        <div className="scroll-y px-4 py-3" style={{ maxHeight: wide ? "62vh" : "auto" }}>
          <p style={{ fontSize: 12.5, color: "var(--ink-3)", marginBottom: "0.75rem" }}>
            crAIg fills this in as you talk. Anything wrong? Tap it and change it.
          </p>

          {kase.canSign && (
            <div
              className="mb-3"
              style={{
                background: "var(--accent-tint)",
                border: "1px solid var(--accent-edge)",
                borderRadius: "var(--radius)",
                padding: "0.7rem 0.8rem",
                fontSize: 12.5,
              }}
            >
              <b style={{ fontWeight: 650 }}>That's everything. Please read it through.</b>
              <ul style={{ margin: "0.4rem 0 0", paddingLeft: "1rem", color: "var(--ink-2)" }}>
                <li>Your name must match your ID exactly</li>
                <li>Check the details you typed into the locked boxes</li>
                <li>Only you know whether the amounts are right</li>
              </ul>
            </div>
          )}

          {filled.length === 0 && (
            <p className="py-6 text-center" style={{ fontSize: 13, color: "var(--ink-3)" }}>
              Nothing here yet — this fills itself in as you chat.
            </p>
          )}

          <div className="space-y-3">
            {filled.map((f) => {
              const value = kase.fields[f.name] ?? "";
              const flag = flagFor(f.name);
              return (
                <div key={f.name}>
                  <SectionLabel>{f.label}</SectionLabel>
                  {editing === f.name ? (
                    <input
                      autoFocus
                      value={draft}
                      onChange={(e) => setDraft(e.target.value)}
                      onBlur={() => {
                        if (draft !== value) onCorrect(f.name, draft);
                        setEditing(null);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") e.currentTarget.blur();
                        if (e.key === "Escape") setEditing(null);
                      }}
                      type={f.type === "date" ? "date" : "text"}
                      className="field mt-1"
                      style={{ fontSize: 14 }}
                    />
                  ) : (
                    <button
                      type="button"
                      onClick={() => {
                        setEditing(f.name);
                        setDraft(value);
                      }}
                      title="Tap to correct"
                      className="w-full text-left mt-0.5"
                      style={{
                        background: "none",
                        border: "none",
                        padding: 0,
                        cursor: "pointer",
                        fontSize: 14,
                        color: "var(--ink)",
                        lineHeight: 1.45,
                      }}
                    >
                      <Typed value={value} />
                    </button>
                  )}
                  {flag && (
                    <p style={{ fontSize: 11.5, color: "var(--warn)", marginTop: 2 }}>{flag}</p>
                  )}
                </div>
              );
            })}

            {Object.entries(kase.checklists).map(([listId, ids]) => {
              const spec = pack.checklists.find((l) => l.id === listId);
              if (!spec || ids.length === 0) return null;
              return (
                <div key={listId}>
                  <SectionLabel>{spec.title}</SectionLabel>
                  <p style={{ fontSize: 14, marginTop: 2, lineHeight: 1.45 }}>
                    {ids.map(optionLabel).join(", ")}
                  </p>
                </div>
              );
            })}

            {buckets.map(([bucket, items]) => {
              const total = items.reduce((n, it) => n + money(it.amount), 0);
              const overThreshold = evidence && total > evidence.threshold;
              return (
                <div key={bucket}>
                  <div className="flex items-baseline gap-2">
                    <SectionLabel>{bucket}</SectionLabel>
                    {total > 0 && (
                      <span className="ml-auto" style={{ fontSize: 12.5, color: "var(--ink-2)" }}>
                        ≈ ${total.toLocaleString("en-AU", { maximumFractionDigits: 0 })}
                      </span>
                    )}
                  </div>

                  <div className="space-y-1.5 mt-1">
                    {items.map((it, i) => (
                      <div
                        key={i}
                        style={{
                          background: "var(--surface-2)",
                          border: "1px solid var(--line)",
                          borderRadius: "var(--radius)",
                          padding: "0.5rem 0.6rem",
                          fontSize: 13,
                        }}
                      >
                        <div>
                          <b style={{ fontWeight: 600 }}>{it.what}</b>
                          {it.amount ? ` — ${it.amount}` : ""}
                          {it.when && <span style={{ color: "var(--ink-3)" }}> · {it.when}</span>}
                        </div>
                        {it.detail && (
                          <div style={{ color: "var(--ink-2)", fontSize: 12.5, marginTop: 2 }}>
                            {it.detail}
                          </div>
                        )}
                        <div
                          style={{
                            fontSize: 12,
                            marginTop: 2,
                            color: it.hasEvidence ? "var(--ok)" : "var(--warn)",
                          }}
                        >
                          {it.hasEvidence ? "receipt kept" : "no receipt — that's usually fine"}
                        </div>
                        {evidence && money(it.amount) > evidence.threshold && (
                          <div style={{ fontSize: 12, color: "var(--ink-3)", marginTop: 2 }}>
                            {evidence.perItem}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  {evidence && total > 0 && (
                    <p
                      style={{
                        fontSize: 12,
                        marginTop: "0.5rem",
                        padding: "0.5rem 0.6rem",
                        borderRadius: "var(--radius)",
                        background: overThreshold ? "var(--warn-tint)" : "var(--ok-tint)",
                        color: "var(--ink-2)",
                      }}
                    >
                      {overThreshold ? evidence.over : evidence.under}
                    </p>
                  )}
                </div>
              );
            })}

            {Object.keys(kase.topicNotes).length > 0 && (
              <div>
                <SectionLabel>Notes</SectionLabel>
                <div className="space-y-1.5 mt-1">
                  {Object.entries(kase.topicNotes).map(([k, v]) => (
                    <div key={k} style={{ fontSize: 13 }}>
                      <b style={{ textTransform: "capitalize", fontWeight: 600 }}>{k}</b>
                      <div style={{ color: "var(--ink-2)" }}>{v}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div>
              <SectionLabel>Receipts</SectionLabel>
              {kase.documentsUnlocked ? (
                <>
                  {kase.documents.length > 0 && (
                    <p style={{ fontSize: 13, color: "var(--ink-2)", margin: "2px 0 6px" }}>
                      {kase.documents.length} file{kase.documents.length === 1 ? "" : "s"} — your{" "}
                      {pack.copy.reviewerNoun} sorts these out, you don't need to label anything
                    </p>
                  )}
                  <button type="button" onClick={onOpenDrop} className="btn btn-quiet w-full mt-1">
                    {kase.documents.length ? "Add more receipts" : "Add your receipts"}
                  </button>
                </>
              ) : (
                <p style={{ fontSize: 12.5, color: "var(--ink-3)", marginTop: 2 }}>
                  You'll add these once you and crAIg have been through everything. No need to dig
                  them out yet.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      <SecurePanel kase={kase} pack={pack} />
      {children}
      <LetCraigDoIt />
    </aside>
  );
}

/**
 * Shows only whether each locked box is filled. There is no route by which
 * a stored value comes back to this page, by design.
 */
function SecurePanel({ kase, pack }: { kase: CaseView; pack: PackView }) {
  const groups = pack.secureGroups.filter((g) => g.required);
  if (groups.length === 0) return null;

  return (
    <div className="card mt-3 px-4 py-3">
      <div className="flex items-center gap-2 mb-2">
        <span aria-hidden="true" style={{ fontSize: 12 }}>
          🔒
        </span>
        <SectionLabel>Private details</SectionLabel>
      </div>

      <div className="space-y-1.5">
        {groups.map((g) => {
          const done =
            g.fields.length > 0 && g.fields.every((f) => kase.secureProvided[`${g.id}.${f.key}`]);
          return (
            <div key={g.id} className="flex items-center gap-2" style={{ fontSize: 13 }}>
              <span style={{ color: done ? "var(--ok)" : "var(--ink-3)" }}>{done ? "✓" : "○"}</span>
              <span>{g.title}</span>
              <span className="ml-auto" style={{ color: "var(--ink-3)", fontSize: 12 }}>
                {done ? "saved" : "not yet"}
              </span>
            </div>
          );
        })}
      </div>

      <p style={{ fontSize: 11.5, color: "var(--ink-3)", marginTop: "0.6rem" }}>
        {pack.copy.secureNote}
      </p>
    </div>
  );
}
